// Signed in, this is where you land: your live games, join a game by code, host one, plus links to
// your profile and (for admins) server onboarding. A Storyteller-eligible user is asked which way
// in they want when they enter a code; everyone else always joins as a player.

import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GAME_CODE_RE, type LiveGameSummary } from "@cc/shared";
import { useAuth } from "../state/auth.js";
import { useGame } from "../state/game.js";
import { api } from "../lib/api.js";
import { LogIn, LogOut, Server, User } from "lucide-react";
import { Button, Input, Label } from "../components/ui/primitives.js";
import { ClockEmblem, PageShell } from "../components/ui/PageShell.js";
import { LiveGames } from "../components/home/LiveGames.js";
import { HostGame } from "../components/home/HostGame.js";

const cardClass = "rounded-xl border border-brass/25 bg-ink-soft/75 p-5 shadow-2xl backdrop-blur-md";
const headingClass = "flex items-center gap-2 font-serif text-lg font-semibold text-parchment";

/** Re-reads the live games list this often, which also keeps "last active" current. */
const REFRESH_MS = 30_000;

export function HomePage() {
  const navigate = useNavigate();
  const { profile, guilds, canStoryteller, isAdmin, signOut } = useAuth();
  const { joinGame, connected } = useGame();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [liveGames, setLiveGames] = useState<LiveGameSummary[]>([]);
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(() => {
    api<{ games: LiveGameSummary[] }>("/api/games/mine")
      .then((body) => {
        setLiveGames(body.games);
        setNow(Date.now());
      })
      .catch(() => {
        /* the list is a convenience; joining by code still works */
      });
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  const normalized = code.trim().toUpperCase();
  const looksValid = GAME_CODE_RE.test(normalized);

  async function go(asStoryteller: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    const ack = await joinGame(normalized, asStoryteller);
    setBusy(false);
    if (!ack.ok) {
      setError(ack.error);
      setAsking(false);
      return;
    }
    navigate(`/game/${normalized}`);
  }

  return (
    <PageShell>
      <div className="space-y-5">
        <header className="text-center">
          <ClockEmblem className="h-12 w-12" />
          <h1 className="mt-3 font-serif text-3xl font-semibold tracking-wide text-parchment">
            Camperdown Clocktower
          </h1>
          <p className="mt-2 text-sm text-parchment-dim">
            Welcome, <span className="text-parchment">{profile?.displayName}</span>
            {canStoryteller && (
              <span className="ml-2 rounded-full border border-brass/50 bg-brass/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-brass">
                Storyteller
              </span>
            )}
          </p>
        </header>

        {guilds.length === 0 && isAdmin && (
          <p className={cardClass + " text-sm text-parchment-dim"}>
            No Discord servers are onboarded yet.{" "}
            <Link to="/admin" className="text-brass hover:underline">
              Onboard one
            </Link>{" "}
            to start hosting games.
          </p>
        )}

        <LiveGames games={liveGames} guilds={guilds} now={now} onChanged={refresh} />

        <section className={cardClass + " space-y-3"}>
          <h2 className={headingClass}>
            <LogIn className="h-4 w-4 text-brass" aria-hidden="true" />
            Join a game
          </h2>
          <Label htmlFor="cc-code">Game code</Label>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!looksValid) return;
              if (canStoryteller) setAsking(true);
              else void go(false);
            }}
          >
            <Input
              id="cc-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC12"
              autoComplete="off"
              maxLength={8}
              className="py-2 text-center font-mono text-lg uppercase tracking-[0.4em]"
            />
            <Button type="submit" variant="primary" disabled={!looksValid || busy || !connected}>
              Join
            </Button>
          </form>

          {asking && (
            <div className="rounded-md border border-brass/50 bg-brass/10 p-2">
              <p className="mb-2 text-xs text-parchment">Join {normalized} as…</p>
              <div className="flex gap-2">
                <Button variant="primary" disabled={busy} onClick={() => void go(true)}>
                  Storyteller
                </Button>
                <Button disabled={busy} onClick={() => void go(false)}>
                  Player
                </Button>
                <Button variant="ghost" onClick={() => setAsking(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {error && <p className="text-xs text-evil">{error}</p>}
          {!connected && <p className="text-xs text-parchment-dim">Connecting…</p>}
        </section>

        {canStoryteller && <HostGame liveGames={liveGames} onChanged={refresh} />}

        <nav className="flex gap-4 text-sm text-parchment-dim">
          <Link to="/profile" className="inline-flex items-center gap-1.5 hover:text-parchment">
            <User className="h-4 w-4" aria-hidden="true" />
            Profile
          </Link>
          {isAdmin && (
            <Link to="/admin" className="inline-flex items-center gap-1.5 hover:text-parchment">
              <Server className="h-4 w-4" aria-hidden="true" />
              Servers
            </Link>
          )}
          <button
            type="button"
            onClick={() => void signOut()}
            className="ml-auto inline-flex items-center gap-1.5 hover:text-parchment"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </nav>
      </div>
    </PageShell>
  );
}
